// The "Vendor Cafe" box on a Bozzuto account (Stephanie 2026-10-30): whether
// this building's invoices go through Vendor Cafe (on unless switched off —
// e.g. Three Collective isn't in it; switch it back on if it's added later)
// and the property name exactly as Vendor Cafe lists it ("The Reed (IL)").
// Shows only on accounts in a Vendor Cafe company, once the database step has run.
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";

interface Resp {
  available: boolean;
  setting: "on" | "off" | null;
  parentSetting: "on" | "off" | null;
  parentName: string | null;
  uses: boolean;
  property: string | null;
  propertyShown: string;
}

export function VendorCafeAccountCard({ clientId, canEdit = true }: { clientId: string; canEdit?: boolean }) {
  const qc = useQueryClient();
  const key = ["clients", clientId, "vendor-cafe"];
  const q = useQuery({ queryKey: key, queryFn: () => api<Resp>(`/clients/${clientId}/vendor-cafe`), retry: false });
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => setName(q.data?.property ?? ""), [q.data?.property]);
  const save = useMutation({
    mutationFn: (body: { setting?: "on" | "off" | null; property?: string | null }) =>
      api<Resp>(`/clients/${clientId}/vendor-cafe`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: (data, body) => {
      qc.setQueryData(key, data);
      void qc.invalidateQueries({ queryKey: ["vendor-cafe"] });
      setError(null);
      setSaved(body.property !== undefined);
    },
    onError: (e) => setError(apiErrorMessage(e, "We couldn't save that. Nothing was changed — try again.")),
  });
  const d = q.data;
  if (!d?.available || (d.setting === null && d.parentSetting === null)) return null;
  const isBuilding = d.parentSetting !== null;
  // Ticking on: a building follows its main account again; a main account is 'on'.
  const turnOn = isBuilding && d.parentSetting === "on" ? null : "on";

  return (
    <section className="rounded-md border border-rule bg-white p-5 sm:p-[22px]" aria-labelledby="vc-account-title">
      <h2 id="vc-account-title" className="text-lg font-semibold tracking-[-0.4px] text-ink">Vendor Cafe</h2>
      <p className="mt-0.5 text-xs text-muted">
        {isBuilding ? `${d.parentName} pays through Vendor Cafe.` : "Invoices are uploaded to Vendor Cafe once every proof is approved."}
      </p>
      <label className="mt-3 flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          className="h-4 w-4 accent-ink"
          checked={d.uses}
          disabled={!canEdit || save.isPending}
          onChange={(e) => save.mutate({ setting: e.target.checked ? turnOn : "off" })}
        />
        {isBuilding ? "This building is in Vendor Cafe" : "Uses Vendor Cafe"}
      </label>
      {!d.uses ? (
        <p className="mt-2 text-xs text-muted">Not in Vendor Cafe — its invoices go out the normal way.</p>
      ) : (
        <form
          className="mt-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate({ property: name.trim() || null });
          }}
        >
          <label className="block text-xs font-medium text-muted">
            Property name in Vendor Cafe
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); setSaved(false); }}
              placeholder={d.propertyShown}
              disabled={!canEdit}
              className="mt-1 block h-9 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
            />
          </label>
          <p className="mt-1 text-xs text-muted">Exactly as Vendor Cafe lists it, e.g. “The Reed (IL)”. Blank = the account name.</p>
          {canEdit && (name.trim() || null) !== (d.property ?? null) ? (
            <Button type="submit" size="sm" className="mt-2" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save name"}</Button>
          ) : saved ? (
            <p className="mt-2 text-xs text-ink">Saved.</p>
          ) : null}
        </form>
      )}
      {error && <p className="mt-2 text-xs text-rust-600">{error}</p>}
    </section>
  );
}
